import React, { useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { FileUploader } from "react-drag-drop-files";
import { LuUpload } from "react-icons/lu";
import pdfToText from "react-pdftotext";
import { queryByPDFdownload } from "./action";
import moment from "moment";
import { toast } from "react-toastify";

const extractPdfQueryId = (text) => {
    if (!text) return "";

    // 1. Match "savedId:", "tariffpdfId:", or "cargoqueryid-" followed by 24-character ObjectId
    const savedIdMatch = text.match(/(?:savedId:|tariffpdfId:|cargoqueryid-)\s*([a-fA-F0-9]{24})/i);
    if (savedIdMatch && savedIdMatch[1]) {
        return savedIdMatch[1];
    }

    // 2. Match "savedId:" with # delimiter (legacy format)
    const startIndex = text.indexOf("savedId:");
    if (startIndex !== -1) {
        const endIndex = text.indexOf("#", startIndex);
        if (endIndex !== -1) {
            let substring = text.substring(startIndex + "savedId:".length, endIndex);
            substring = substring.replace(/\s+/g, "");
            if (substring) return substring;
        }
    }

    // 3. Match "ID:" followed by 24-character ObjectId
    const idMatch = text.match(/ID:\s*([a-fA-F0-9]{24})/i);
    if (idMatch && idMatch[1]) {
        return idMatch[1];
    }

    // 4. Match any 24-character hex ObjectId string anywhere in document text
    const hexMatch = text.match(/([a-fA-F0-9]{24})/);
    if (hexMatch && hexMatch[1]) {
        return hexMatch[1];
    }

    return "";
};

const PdfSourceGuide = ({ isLaytimeStatement }) => {
    const name = isLaytimeStatement ? "Laytime Statement" : "Statement of Facts";
    const tab = isLaytimeStatement ? "Smart Laytime" : "Smart Statement of Facts";
    return (
        <div className="mt-3 border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-700">
            <div className="font-semibold text-slate-900 mb-1">Where to get the PDF</div>
            <ul className="list-disc pl-5 space-y-1">
                <li>In <strong>Operations</strong>, open an existing operation, open its {name} panel, and choose <strong>Download PDF</strong> at the top of that panel.</li>
                <li>Or in <strong>{tab}</strong>, open a saved {name} (or create one) and choose <strong>Download PDF</strong>.</li>
            </ul>
            <p className="mt-1">Upload that saved PDF here to load its {name} data.</p>
        </div>
    );
};

export function UploadPDFModalCargoCargo(props) {
    const {
        handleClose,
        title = "Upload PDF",
        open,
        isLaytimeStatement,
        setValues,
        activeTab,
        selectedOperationIndex,
        setWarning,
        warning,
    } = props;
    const [error, setError] = useState("");
    const [fileUploading, setFileUploading] = useState("");
    const [fileData, setFileData] = useState(null);
    const fileTypes = ["PDF"];
    const handleChange = async (file) => {
        setFileUploading("Pdf file Uploading...");
        const queryid = await pdfToText(file)
            .then((text) => {
                return extractPdfQueryId(text);
            })
            .catch((err) => {
                setError("invalid file! Please upload valid file");
                setFileUploading("");
            });
        if (!queryid) {
            setError("invalid file! Please upload valid file");
            return;
        } else {
            setError("");
        }
        try {
            const details = await queryByPDFdownload(queryid, isLaytimeStatement);
            if (details?.data) {
                setFileData(details?.data);
                setFileUploading("File Uploaded Successfully!");
            } else {
                setError("invalid file! Please upload valid file!!");
                setFileUploading("");
            }
        } catch (err) {
            setError("Something went wrong!");
            setFileUploading("");
        }
    };
    const CustomSection = () => {
        return (
            <div className="upload-container" id="drop-area">
                <LuUpload size={18} aria-hidden="true" />
                <span className="upload-container-title">Choose a PDF or drag it here</span>
                {!error ? (
                    fileUploading ? (
                        <p aria-live="polite">{fileUploading}</p>
                    ) : (
                        <p>PDF files only</p>
                    )
                ) : (
                    ""
                )}
                {error ? <p className="!text-rose-600" role="alert">{error}</p> : ""}
            </div>
        );
    };
    const onClose = () => {
        handleClose();
        setError("");
        setFileUploading("");
    };
    const onSubmit = () => {
        if (fileData) {
            setValues((prev) => {
                const newState = { ...prev };

                const cargoKey = activeTab === 0 ? "customerCargo" : "vendorCargo";

                if (!newState[cargoKey]) newState[cargoKey] = {};
                if (!newState[cargoKey].cargoList) newState[cargoKey].cargoList = [];

                const updatedCargoList = [...newState[cargoKey].cargoList];
                const currentItem = { ...updatedCargoList[selectedOperationIndex] };

                if (isLaytimeStatement) {
                    const currentLaytimeStatement = currentItem?.laytimeStatement;

                    if (currentLaytimeStatement) {
                        const laytimeStart = moment(fileData?.laytimeStarts);
                        const laytimeStop = moment(fileData?.laytimeStops);
                        const eventTable = fileData?.eventTable || [];

                        const itemArrival = moment(currentItem?.arrivalTime);
                        const itemDeparture = moment(currentItem?.departureTime);

                        const newWarnings = { ...warning };
                        const itemId = currentItem?._id;
                        const toastWarnings = [];

                        if (!newWarnings[itemId]) {
                            newWarnings[itemId] = {};
                        }

                        /* -------------------------
                           VALIDATE LAYTIME START
                        -------------------------- */
                        if (laytimeStart.isBefore(itemArrival)) {
                            newWarnings[itemId].laytimeStarts = "Laytime Start cannot be before Arrival Time";
                            toastWarnings.push("Laytime Start cannot be before Arrival Time");
                        } else if (laytimeStart.isAfter(itemDeparture)) {
                            newWarnings[itemId].laytimeStarts = "Laytime Start cannot be after Departure Time";
                            toastWarnings.push("Laytime Start cannot be after Departure Time");
                        }

                        /* -------------------------
                           VALIDATE LAYTIME STOP
                        -------------------------- */
                        if (laytimeStop.isBefore(itemArrival)) {
                            newWarnings[itemId].laytimeStops = "Laytime Stop cannot be before Arrival Time";
                            toastWarnings.push("Laytime Stop cannot be before Arrival Time");
                        } else if (laytimeStop.isAfter(itemDeparture)) {
                            newWarnings[itemId].laytimeStops = "Laytime Stop cannot be after Departure Time";
                            toastWarnings.push("Laytime Stop cannot be after Departure Time");
                        }

                        /* -------------------------
                           VALIDATE EVENT TABLE
                        -------------------------- */
                        eventTable.forEach((evt, idx) => {
                            const fromM = moment(evt.fromTime);
                            const toM = moment(evt.toTime);
                            const rowWarns = {};

                            if (fromM.isBefore(itemArrival)) {
                                rowWarns.fromTime = "Event fromTime cannot be before Arrival Time";
                            } else if (fromM.isAfter(itemDeparture)) {
                                rowWarns.fromTime = "Event fromTime cannot be after Departure Time";
                            }

                            if (toM.isBefore(itemArrival)) {
                                rowWarns.toTime = "Event toTime cannot be before Arrival Time";
                            } else if (toM.isAfter(itemDeparture)) {
                                rowWarns.toTime = "Event toTime cannot be after Departure Time";
                            }

                            if (Object.keys(rowWarns).length > 0) {
                                if (!newWarnings[itemId].eventTable) {
                                    newWarnings[itemId].eventTable = {};
                                }

                                newWarnings[itemId].eventTable[idx] = rowWarns;

                                Object.values(rowWarns).forEach((m) =>
                                    toastWarnings.push(`Row ${idx + 1}: ${m}`)
                                );
                            }
                        });

                        /* -------------------------
                           SHOW TOAST IF ANY WARNINGS
                        -------------------------- */
                        if (toastWarnings.length > 0) {
                            const formattedToast = (
                                <div>
                                    <strong className="text-rose-600">⚠️ Validation Warning</strong>
                                    <ul className="mt-1 list-disc list-inside text-sm text-rose-700">
                                        {toastWarnings.map((msg, i) => (
                                            <li key={i}>{msg}</li>
                                        ))}
                                    </ul>
                                </div>
                            );

                            toast.error(formattedToast, {
                                autoClose: false,
                                position: "bottom-left",
                                className:
                                    "max-w-sm sm:max-w-md md:max-w-2xl w-[450px] text-black border border-rose-300 rounded-md p-4 bg-rose-100 shadow-lg",
                                bodyClassName: "overflow-x-auto",
                            });
                        }

                        setWarning(newWarnings);
                    }

                    /* -------------------------
                       APPLY PARSED LAYTIME DATA
                    -------------------------- */
                    const {
                        vesselName: _vn,
                        arrivalTime: parsedArrival,
                        departureTime: parsedDeparture,
                        cargoDesc,
                        voyageNo,
                        shipmentId,
                        operation,
                        laytimePort,
                        charterer,
                        billOfLadingDate,
                        ...rest
                    } = fileData;

                    currentItem.laytimeStatement = {
                        ...currentItem.laytimeStatement,
                        ...rest,
                    };
                } else {
                    currentItem.statementOfFacts = {
                        ...currentItem.statementOfFacts,
                        ...fileData,
                    };
                }

                updatedCargoList[selectedOperationIndex] = currentItem;

                newState[cargoKey] = {
                    ...newState[cargoKey],
                    cargoList: updatedCargoList,
                };

                return newState;
            });
            onClose();
        } else {
            setError("invalid file! Please upload valid file");
            setFileUploading("");
        }
    };
    return (
        <div>
            <Dialog
                open={open}
                fullWidth
                maxWidth="sm"
                onClose={() => onClose()}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" className="font-family">
                    {title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        component="div"
                        id="alert-dialog-description"
                        className="font-family"
                    >
                        <FileUploader
                            classes="pdf-upload-trigger"
                            dropMessageStyle={{ color: "#000", backgroundColor: "red" }}
                            children={<CustomSection />}
                            multiple={false}
                            handleChange={handleChange}
                            name="file"
                            label="Upload or drop a file right here"
                            types={fileTypes}
                        />
                    </DialogContentText>
                    <PdfSourceGuide isLaytimeStatement={isLaytimeStatement} />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            onClose();
                        }}
                        color="primary"
                        className="font-family"
                        autoFocus
                    >
                        Cancel
                    </Button>
                    {fileUploading ? (
                        <Button
                            onClick={() => {
                                onSubmit();
                            }}
                            color="primary"
                            className="font-family"
                            autoFocus
                        >
                            Confirm
                        </Button>
                    ) : (
                        ""
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
}

export function UploadPDFModal(props) {
    const {
        handleClose,
        title = "Upload PDF",
        open,
        isLaytimeStatement,
        setState,
        selectedOperationIndex,
        setWarning,
        warning,
    } = props;
    const [error, setError] = useState("");
    const [fileUploading, setFileUploading] = useState("");
    const [fileData, setFileData] = useState(null);

    const fileTypes = ["PDF"];
    const handleChange = async (file) => {
        setFileUploading("Pdf file Uploading...");
        const queryid = await pdfToText(file)
            .then((text) => {
                return extractPdfQueryId(text);
            })
            .catch((err) => {
                setError("invalid file! Please upload valid file");
                setFileUploading("");
            });
        if (!queryid) {
            setError("invalid file! Please upload valid file");
            return;
        } else {
            setError("");
        }
        try {
            const details = await queryByPDFdownload(queryid, isLaytimeStatement);
            if (details?.data) {
                setFileData(details?.data);
                setFileUploading("File Uploaded Successfully!");
            } else {
                setError("invalid file! Please upload valid file!!");
                setFileUploading("");
            }
        } catch (err) {
            setError("Something went wrong!");
            setFileUploading("");
        }
    };
    const CustomSection = () => {
        return (
            <div className="upload-container" id="drop-area">
                <LuUpload size={18} aria-hidden="true" />
                <span className="upload-container-title">Choose a PDF or drag it here</span>
                {!error ? (
                    fileUploading ? (
                        <p aria-live="polite">{fileUploading}</p>
                    ) : (
                        <p>PDF files only</p>
                    )
                ) : (
                    ""
                )}
                {error ? <p className="!text-rose-600" role="alert">{error}</p> : ""}
            </div>
        );
    };
    const onClose = () => {
        handleClose();
        setError("");
        setFileUploading("");
    };
    const onSubmit = () => {
        if (fileData) {
            setState((prev) => {
                const newState = { ...prev };
                const updatedCargoList = [...newState?.cargoList];
                const currentItem = { ...updatedCargoList[selectedOperationIndex] };
                if (isLaytimeStatement) {
                    const currentLaytimeStatement = currentItem?.laytimeStatement;

                    if (currentLaytimeStatement) {
                        const laytimeStart = moment(fileData?.laytimeStarts);
                        const laytimeStop = moment(fileData?.laytimeStops);
                        const eventTable = fileData?.eventTable;
                        const arrivalTime = moment(currentItem?.arrivalTime);
                        const departureTime = moment(currentItem?.departureTime);

                        const newWarnings = { ...warning };
                        let toastWarnings = [];

                        if (!newWarnings[currentItem?._id]) {
                            newWarnings[currentItem._id] = {};
                        }

                        if (laytimeStart.isBefore(arrivalTime)) {
                            newWarnings[currentItem._id].laytimeStarts =
                                "Laytime Start cannot be earlier than Arrival Time";
                            toastWarnings.push("Laytime Start cannot be earlier than Arrival Time");
                        } else if (laytimeStart.isAfter(departureTime)) {
                            newWarnings[currentItem._id].laytimeStarts =
                                "Laytime Start cannot be later than Departure Time";
                            toastWarnings.push("Laytime Start cannot be later than Departure Time");
                        }

                        if (laytimeStop.isBefore(arrivalTime)) {
                            newWarnings[currentItem._id].laytimeStops =
                                "Laytime Stop cannot be earlier than Arrival Time";
                            toastWarnings.push("Laytime Stop cannot be earlier than Arrival Time");
                        } else if (laytimeStop.isAfter(departureTime)) {
                            newWarnings[currentItem._id].laytimeStops =
                                "Laytime Stop cannot be later than Departure Time";
                            toastWarnings.push("Laytime Stop cannot be later than Departure Time");
                        }

                        eventTable.forEach((evt, idx) => {
                            const fromM = moment(evt.fromTime);
                            const toM = moment(evt.toTime);
                            let rowWarns = {};

                            if (fromM.isBefore(arrivalTime)) {
                                rowWarns.fromTime = "Event ‘fromTime’ cannot be before Arrival Time";
                            } else if (fromM.isAfter(departureTime)) {
                                rowWarns.fromTime = "Event ‘fromTime’ cannot be after Departure Time";
                            }

                            if (toM.isBefore(arrivalTime)) {
                                rowWarns.toTime = "Event ‘toTime’ cannot be before Arrival Time";
                            } else if (toM.isAfter(departureTime)) {
                                rowWarns.toTime = "Event ‘toTime’ cannot be after Departure Time";
                            }

                            if (Object.keys(rowWarns).length > 0) {
                                if (!newWarnings[currentItem._id].eventTable) {
                                    newWarnings[currentItem._id].eventTable = {};
                                }
                                newWarnings[currentItem._id].eventTable[idx] = rowWarns;
                                Object.values(rowWarns).forEach(msg => toastWarnings.push(`Row ${idx + 1}: ${msg}`));
                            }
                        });

                        if (toastWarnings.length > 0) {
                            const formattedToast = (
                                <div>
                                    <strong className="text-rose-600">⚠️ Validation Warning</strong>
                                    <ul className="mt-1 list-disc list-inside text-sm text-rose-700">
                                        {toastWarnings.map((msg, i) => (
                                            <li key={i}>{msg}</li>
                                        ))}
                                    </ul>
                                </div>
                            );

                            toast.error(formattedToast, {
                                autoClose: false,
                                position: "bottom-left",
                                className:
                                    "max-w-sm sm:max-w-md md:max-w-2xl w-[450px] text-black border border-rose-300 rounded-md p-4 bg-rose-100 shadow-lg",
                                bodyClassName: "overflow-x-auto",
                            });
                        }

                        setWarning(newWarnings);
                    }

                    const { vesselName, arrivalTime, departureTime, cargoDesc, voyageNo, shipmentId, operation, laytimePort, charterer, billOfLadingDate, ...rest } = fileData;
                    currentItem.laytimeStatement = {
                        ...currentItem.laytimeStatement,
                        ...rest
                    };
                } else {
                    currentItem.statementOfFacts = {
                        ...currentItem.statementOfFacts,
                        ...fileData
                    };
                }

                updatedCargoList[selectedOperationIndex] = currentItem;

                return {
                    ...newState,
                    cargoList: updatedCargoList,
                };
            });
            onClose();
        } else {
            setError("invalid file! Please upload valid file");
            setFileUploading("");
        }
    };
    return (
        <div>
            <Dialog
                open={open}
                fullWidth
                maxWidth="sm"
                onClose={() => onClose()}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" className="font-family">
                    {title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText
                        component="div"
                        id="alert-dialog-description"
                        className="font-family"
                    >
                        <FileUploader
                            classes="pdf-upload-trigger"
                            dropMessageStyle={{ color: "#000", backgroundColor: "red" }}
                            children={<CustomSection />}
                            multiple={false}
                            handleChange={handleChange}
                            name="file"
                            label="Upload or drop a file right here"
                            types={fileTypes}
                        />
                    </DialogContentText>
                    <PdfSourceGuide isLaytimeStatement={isLaytimeStatement} />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            onClose();
                        }}
                        color="primary"
                        className="font-family"
                        autoFocus
                    >
                        Cancel
                    </Button>
                    {fileUploading ? (
                        <Button
                            onClick={() => {
                                onSubmit();
                            }}
                            color="primary"
                            className="font-family"
                            autoFocus
                        >
                            Confirm
                        </Button>
                    ) : (
                        ""
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
}
